import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import Alerty from '../../common/Alerty';
import ExpenseForm from './ExpenseForm';
import { Button } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const mapCategories = (items) => (items || []).map((c) => ({
    category_id: c.id,
    category_name: c.name,
    code: c.code,
    requires_vendor: !!c.requires_vendor,
    expense_type: c.is_vehicle_related ? 'VEHICLE' : (c.is_office_related ? 'OFFICE' : '')
}));

const normalizeArrayResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const ExpenseFormPage = () => {
    const { expenseId } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();

    const [loading, setLoading] = useState(Boolean(expenseId));
    const [expense, setExpense] = useState(null);
    const [categories, setCategories] = useState([]);
    const [cars, setCars] = useState([]);
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ color: '', message: '' });

    const authHeaders = React.useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token]
    );

    const showAlert = useCallback((color, message) => {
        setAlert({ color, message });
        setOpen(true);
    }, []);

    const fetchReferenceData = useCallback(async () => {
        try {
            const [categoriesRes, carsRes] = await Promise.all([
                axios.get(`${rootUrl}/api/expenses/categories`, { headers: authHeaders }),
                axios.get(`${rootUrl}/cars`, { headers: authHeaders })
            ]);

            if (categoriesRes.data?.success) {
                setCategories(mapCategories(categoriesRes.data.data || []));
            }

            setCars(normalizeArrayResponse(carsRes.data));
        } catch (err) {
            console.error('Error loading expense reference data:', err);
        }
    }, [authHeaders]);

    const fetchExpense = useCallback(async () => {
        if (!expenseId) {
            setExpense(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/expenses/${expenseId}`, { headers: authHeaders });
            if (response.data?.success) {
                setExpense(response.data.data || null);
            } else {
                throw new Error(response.data?.message || 'Expense not found');
            }
        } catch (err) {
            console.error('Error loading expense for editing:', err);
            showAlert('error', err.response?.data?.message || err.message || 'Failed to load expense');
            navigate('/expenses');
        } finally {
            setLoading(false);
        }
    }, [authHeaders, expenseId, navigate, showAlert]);

    useEffect(() => {
        fetchReferenceData();
    }, [fetchReferenceData]);

    useEffect(() => {
        fetchExpense();
    }, [fetchExpense]);

    const handleSuccess = useCallback((savedExpense) => {
        const savedId = savedExpense?.expense_id ?? savedExpense?.id ?? expenseId;
        if (savedId) {
            navigate(`/expenses/${savedId}`);
        } else {
            navigate('/expenses');
        }
    }, [expenseId, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
                <Alerty open={open} color={alert.color} message={alert.message} setOpen={setOpen} />
                <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="rounded-xl border border-gray-100 bg-white shadow-lg p-6 text-center text-gray-600">
                        Loading expense form...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] via-white to-[#EDE4DA]">
            <Alerty open={open} color={alert.color} message={alert.message} setOpen={setOpen} />
            <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6">
                <div className="flex justify-end">
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/expenses')}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 border-0 text-sm font-semibold rounded-sm px-4 py-2 shadow-none"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Back to expenses
                    </Button>
                </div>

                <ExpenseForm
                    asPage
                    open
                    onClose={() => navigate('/expenses')}
                    onSuccess={handleSuccess}
                    expense={expense}
                    categories={categories}
                    cars={cars}
                />
            </div>
        </div>
    );
};

export default ExpenseFormPage;
